/** Copy a system block into one tenant, creating a D4 shadow.
 *
 *  This is the safe door. Editing a system block changes it for every tenant
 *  that has not overridden it — which is why that needs
 *  `console_notification_library_manage`. Cloning needs only the ordinary block
 *  permission, because it writes a tenant-scoped row and leaves the shared one
 *  alone; `resolve_block_refs` then substitutes the clone wherever the system
 *  block is referenced under its own codename, including inside system lists.
 *
 *  The clone deliberately keeps the codename — that *is* the shadowing
 *  mechanism, so it is not editable here.
 */
import { Alert, Button, Stack, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as blocksApi from '@/api/notificationBlocks';
import type { NotificationBlockListItem } from '@/api/notificationBlocks';
import { AppDialog } from '@/components/AppDialog';
import { useSnackbar } from '@/components/Snackbar';
import { TenantPicker, type TenantPickerValue } from '@/components/TenantPicker/TenantPicker';
import { singlePageTenant } from '@/scope/pageTenants';

interface Props {
  open: boolean;
  block: Pick<NotificationBlockListItem, 'id' | 'codename' | 'name'>;
  onClose: () => void;
  onCloned?: (id: number) => void;
  /** The blocks page's operators (D9): the one is pre-selected as the clone
   *  target on every open, all are featured; see `useCreateDialogTenant`. */
  pageTenants?: TenantPickerValue[];
}

export function CloneBlockDialog({ open, block, onClose, onCloned, pageTenants }: Props) {
  const [tenant, setTenant] = useState<TenantPickerValue | null>(() => singlePageTenant(pageTenants));
  useEffect(() => {
    if (open) setTenant(singlePageTenant(pageTenants));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const snackbar = useSnackbar();
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => blocksApi.clone(block.id, tenant!.id),
    onSuccess: (created) => {
      snackbar.show(`Cloned to ${created.tenant?.code ?? 'tenant'}.`, 'success');
      qc.invalidateQueries({ queryKey: ['notification_blocks'] });
      onCloned?.(created.id);
      onClose();
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Clone failed.', 'error'),
  });

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      size="sm"
      title={`Clone “${block.codename}” to a tenant`}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!tenant || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Cloning…' : 'Clone'}
          </Button>
        </>
      }
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          The copy keeps the codename <code>{block.codename}</code> — that is what makes it
          override the system block for this tenant, everywhere it is referenced. Other tenants
          keep rendering the system one.
        </Typography>
        <TenantPicker value={tenant} featured={pageTenants} onChange={setTenant} label="Clone into" />
        <Alert severity="info" variant="outlined">
          The copy stops tracking the notification pack. Later pack updates to this block will
          reach every tenant except this one.
        </Alert>
      </Stack>
    </AppDialog>
  );
}
